import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocalization } from '../context/LocalizationContext';
import { useSession } from '../hooks/useSession';
import { ApiManager } from '../services/ApiManager';
import { EXPERIMENT_TOPICS } from '../constants/experiments';
import parentFrameMessaging from '../services/ParentFrameMessaging';

// Mobile device detection function
const isMobileDevice = (): boolean => {
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;

  // Check for mobile user agents
  const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;

  // Also check for touch capability and screen size as fallback
  const hasTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const isSmallScreen = window.innerWidth <= 768;

  return mobileRegex.test(userAgent) || (hasTouchScreen && isSmallScreen);
};

// Cleanup function to remove old playmerce data when timer is over 24 hours old
const cleanupOldPlaymerceData = (): void => {
  const timerEndTime = localStorage.getItem('playmerce_timer_end_time');

  if (!timerEndTime) {
    return;
  }

  const endTime = parseInt(timerEndTime, 10);
  const currentTime = Date.now();
  const twentyFourHoursInMs = 24 * 60 * 60 * 1000;

  // Check if timer end time is over 24 hours ago
  if (currentTime - endTime > twentyFourHoursInMs) {
    // Remove localStorage items (except playmerce_dismissed)
    const localStorageKeysToRemove = [
      'playmerce_event_count',
      'playmerce_game_complete',
      'playmerce_timer_end_time',
      'playmerce_feed_loaded',
      'playmerce_game_scroll_position',
      'playmerce_session_started',
      'playmerce_session_start_time',
      'playmerce_product_order',
      'playmerce_game_state',
    ];

    localStorageKeysToRemove.forEach((key) => {
      if (localStorage.getItem(key) !== null) {
        localStorage.removeItem(key);
      }
    });
  }
};

const Default: React.FC = () => {
  const navigate = useNavigate();
  const { cookieStatus, page } = useLocalization();
  const { setTestMode } = useSession();
  const [eligibilityChecked, setEligibilityChecked] = useState(false);
  const [eventCountIncremented, setEventCountIncremented] = useState(false);
  const [testModeTriggered, setTestModeTriggered] = useState(false);

  // Note: locale messaging is now initialized at the routes level

  // Send handshake message to parent on mount with retry until handshake received
  useEffect(() => {
    const stopRetry = parentFrameMessaging.sendIframeReadyWithRetry();
    return stopRetry;
  }, []);

  // Increment event count when cookies are enabled (only once per session)
  useEffect(() => {
    if (cookieStatus === true && !eventCountIncremented) {
      try {
        const existingCount = localStorage.getItem('playmerce_event_count');
        if (existingCount === null) {
          localStorage.setItem('playmerce_event_count', '1');
        } else {
          const currentCount = parseInt(existingCount, 10);
          localStorage.setItem('playmerce_event_count', (currentCount + 1).toString());
        }
        setEventCountIncremented(true);
      } catch (error) {
        console.warn('Failed to increment playmerce_event_count:', error);
      }
    }
  }, [cookieStatus, eventCountIncremented]);

  // Check if game should be shown after handshake is received
  useEffect(() => {
    const checkGameEligibility = async () => {
      // Clean up old data before checking eligibility
      cleanupOldPlaymerceData();

      // Skip if already checked or cookies status not received yet
      if (eligibilityChecked) {
        console.log('[PMC] Eligibility check skipped: already checked');
        return;
      }

      if (cookieStatus === null) {
        console.log('[PMC] Eligibility check skipped: cookies status not received yet');
        return;
      }

      try {
        // 1. Check that playmerce_dismissed does not exist in localStorage
        const dismissed = localStorage.getItem('playmerce_dismissed');
        if (dismissed === 'true') {
          console.log('[PMC] Widget not shown: previously dismissed');
          setEligibilityChecked(true);
          return;
        }

        // 2. Check that playmerce_game_complete does not exist in localStorage
        const gameComplete = localStorage.getItem('playmerce_game_complete');
        if (gameComplete === 'true') {
          console.log('[PMC] Widget not shown: game already completed');
          setEligibilityChecked(true);
          return;
        }

        // 3. Check if timer has expired
        const timerEndTime = localStorage.getItem('playmerce_timer_end_time');
        if (timerEndTime) {
          const endTime = parseInt(timerEndTime, 10);
          const currentTime = Date.now();
          if (currentTime > endTime) {
            console.log('[PMC] Widget not shown: timer expired', { currentTime, endTime });
            setEligibilityChecked(true);
            return;
          }
        }

        // 4. Check whether cookies are enabled (from handshake response)
        if (!cookieStatus) {
          console.log('[PMC] Widget not shown: cookies not enabled');
          setEligibilityChecked(true);
          return;
        }

        // 5. Check if device is mobile
        if (!isMobileDevice()) {
          console.log('[PMC] Widget not shown: not a mobile device');
          setEligibilityChecked(true);
          return;
        }

        // 6. Check that the page does not contain "cart" or "checkout"
        if (page && (page.toLowerCase().includes('cart') || page.toLowerCase().includes('checkout'))) {
          console.log(`[PMC] Widget not shown: page contains cart or checkout (${page})`);
          setEligibilityChecked(true);
          return;
        }

        // 7. Get session ID first
        const sessionResponse = await ApiManager.customer.getSession();
        const sessionId = sessionResponse.data.id;

        // 8. Fetch experiments for all available topics
        const topics = Object.values(EXPERIMENT_TOPICS);
        const experimentAssignments: Record<string, string> = {};

        // Fetch experiment assignments for all topics in parallel
        const experimentPromises = topics.map(async (topic) => {
          try {
            const response = await ApiManager.customer.getExperiment(topic, sessionId);
            experimentAssignments[response.data.topic] = response.data.group;
          } catch (error) {
            // Don't include failed topics in assignments
          }
        });

        await Promise.allSettled(experimentPromises);

        // Format experiments as comma-separated key:value pairs
        const experiments = Object.entries(experimentAssignments)
          .map(([topic, group]) => `${topic}:${group}`)
          .join(',');

        // 9. Check the eligible endpoint with session ID and experiments
        const eligibilityResponse = await ApiManager.customer.getScrollCollectEligibility(experiments, sessionId);

        if (!eligibilityResponse.data.eligible) {
          console.log('[PMC] Widget not shown: not eligible');
          setEligibilityChecked(true);
          return;
        }

        // 10. Compare playmerce_event_count in sessionStorage to remainingEventCount
        const eventCountStr = localStorage.getItem('playmerce_event_count');
        const currentEventCount = eventCountStr ? parseInt(eventCountStr, 10) : 0;
        const remainingEventCount = eligibilityResponse.data.remainingEventCount;

        console.log(`[PMC] Event count check: current=${currentEventCount}, remaining=${remainingEventCount}`);

        // If remainingEventCount is null, assume unlimited events allowed
        if (remainingEventCount !== null && currentEventCount < remainingEventCount) {
          console.log(`[PMC] Widget not shown: event count not enough (${currentEventCount} < ${remainingEventCount})`);
          setEligibilityChecked(true);
          return;
        }

        // All checks passed - show the game
        console.log('[PMC] All eligibility checks passed - navigating to widget');
        navigate('/game');
        setEligibilityChecked(true);
      } catch (error) {
        console.error('[PMC] Failed to check eligibility:', error);
        setEligibilityChecked(true);
      }
    };
    checkGameEligibility();
  }, [cookieStatus, page, eligibilityChecked, navigate]);

  // Keep existing test mode handling - wait for handshake before navigating
  useEffect(() => {
    const unsubscribe = parentFrameMessaging.onTestMode(() => {
      setTestMode(true);
      setTestModeTriggered(true);
      // Only navigate if handshake has been received (cookieStatus is not null)
      if (cookieStatus !== null) {
        console.log('[PMC] Test mode triggered - navigating to game');
        navigate('/game');
      }
    });
    return unsubscribe;
  }, [navigate, setTestMode, cookieStatus]);

  // Navigate to game in test mode once handshake is received
  useEffect(() => {
    if (testModeTriggered && cookieStatus !== null) {
      console.log('[PMC] Test mode triggered later - navigating to game');
      console.log('cookieStatus', cookieStatus);
      navigate('/game');
    }
  }, [testModeTriggered, cookieStatus, navigate]);

  return <div></div>;
};

export default Default;
